/**
 * Кривая постоянного произведения с виртуальными резервами.
 *
 * Этот модуль — единственный источник истины для котировки: его используют тесты
 * контрактов, Mini App и бэкенд. Все величины — целые в наноединицах, все деления
 * явные, поэтому результат функции побитово совпадает с результатом контракта.
 *
 * Механика описана в docs/04-onchain-architecture.md (разделы «Математика кривой»
 * и «Защита от снайпинга»), ADR-0006 (сид ликвидности) и ADR-0007 (антиснайп).
 */
import {
  BPS_DENOM,
  CurveConfig,
  NANO,
  devBuyLimit,
  poolReserve,
} from './config';
import { fail } from './errors';
import { ceilDiv, max, min, mulDivCeil, mulDivFloor } from './int';

export type CurvePhase = 'Initializing' | 'Active' | 'Graduating' | 'Graduated';

export interface CurveState {
  phase: CurvePhase;
  /** TON в резерве кривой без комиссий: сид плюс публичные покупки. */
  realTon: bigint;
  /** Часть `realTon`, пришедшая сидом ликвидности. База 5% — это `realTon - seedTon`. */
  seedTon: bigint;
  /** Сид сверх ёмкости кривой: дополнительная TON-нога будущего пула. */
  excessSeedTon: bigint;
  /** Антиснайп-накопитель: в `FeeVault` не идёт, на градуации уходит в сид пула. */
  antiSnipeTon: bigint;
  /** Жетоны, ушедшие с кривой: публика, транш сида и dev-buy. */
  sold: bigint;
  /** Транш сида: жетоны, которые никогда не попадают на кошелёк деплоера. */
  seedJettons: bigint;
  /** Жетоны, купленные деплоером в операции запуска. */
  devBuyJettons: bigint;
  /** Момент перехода в `Active` — точка отсчёта антиснайп-окна. */
  activatedAt: number | null;
}

export function initialState(): CurveState {
  return {
    phase: 'Initializing',
    realTon: 0n,
    seedTon: 0n,
    excessSeedTon: 0n,
    antiSnipeTon: 0n,
    sold: 0n,
    seedJettons: 0n,
    devBuyJettons: 0n,
    activatedAt: null,
  };
}

export interface Reserves {
  /** `X = V0 + real_ton`. */
  x: bigint;
  /** `Y = J0 - sold`. */
  y: bigint;
}

export function reserves(config: CurveConfig, state: CurveState): Reserves {
  return {
    x: config.virtualTon + state.realTon,
    y: config.virtualJettons - state.sold,
  };
}

/** `k = X * Y`. Из-за округления вниз фактическое `k` не убывает. */
export function productK(config: CurveConfig, state: CurveState): bigint {
  const { x, y } = reserves(config, state);
  return x * y;
}

/** Цена бесконечно малой сделки: нанотоны за один целый жетон. */
export function spotPrice(config: CurveConfig, state: CurveState): bigint {
  const { x, y } = reserves(config, state);
  return mulDivFloor(x, NANO, y);
}

/** Публичные TON на кривой: база для 5% при градуации, без сида. */
export function publicTon(state: CurveState): bigint {
  return max(0n, state.realTon - state.seedTon);
}

/** TON, которые кривая ещё может принять в резерв до порога градуации. */
export function remainingTonCapacity(config: CurveConfig, state: CurveState): bigint {
  return max(0n, config.graduationThreshold - state.realTon);
}

/** Жетоны, которые кривая ещё может продать. */
export function remainingJettons(config: CurveConfig, state: CurveState): bigint {
  return max(0n, config.curveSupply - state.sold);
}

/** Порог закрыт: либо собран весь TON, либо распродана вся продаваемая часть. */
export function isCurveClosed(config: CurveConfig, state: CurveState): boolean {
  return remainingTonCapacity(config, state) === 0n || remainingJettons(config, state) === 0n;
}

export interface Fill {
  /** TON, фактически вошедшие в резерв. */
  dxUsed: bigint;
  /** Жетоны, ушедшие с кривой. */
  jettons: bigint;
  /** TON, которые кривая принять не смогла: перелёт через порог. */
  leftover: bigint;
}

/**
 * Обмен `dx` TON на жетоны по формуле `dy = floor(Y * dx / (X + dx))` с двумя
 * потолками: порог градуации по TON и остаток продаваемой части по жетонам.
 * Перелёт не отбивается целиком, а исполняется частично — излишек возвращается
 * вызывающему коду в `leftover`.
 */
export function fillOnCurve(config: CurveConfig, state: CurveState, dx: bigint): Fill {
  if (dx <= 0n) {
    return { dxUsed: 0n, jettons: 0n, leftover: max(0n, dx) };
  }
  const { x, y } = reserves(config, state);
  const jettonCap = remainingJettons(config, state);
  let dxUsed = min(dx, remainingTonCapacity(config, state));
  let jettons = dxUsed > 0n ? mulDivFloor(y, dxUsed, x + dxUsed) : 0n;

  if (jettons > jettonCap) {
    // Жетонов нужно больше, чем осталось: берём минимальный `dx`, которым
    // остаток ещё выкупается целиком, остальное уходит в `leftover`.
    const dxForCap = jettonCap > 0n ? mulDivCeil(x, jettonCap, y - jettonCap) : 0n;
    dxUsed = min(dxUsed, dxForCap);
    jettons = min(jettonCap, dxUsed > 0n ? mulDivFloor(y, dxUsed, x + dxUsed) : 0n);
  }

  return { dxUsed, jettons, leftover: dx - dxUsed };
}

/**
 * Ставка антиснайп-комиссии: `9900 * (5 - t) / 5` bps, где `t` — целые секунды
 * от перехода кривой в `Active`. До открытия торговли и после окна — ноль.
 */
export function antiSnipeFeeBps(
  config: CurveConfig,
  state: CurveState,
  now: number,
): bigint {
  if (state.activatedAt === null) {
    return 0n;
  }
  const window = BigInt(config.antiSnipeWindowSeconds);
  const elapsed = BigInt(Math.max(0, now - state.activatedAt));
  if (elapsed >= window) {
    return 0n;
  }
  return mulDivFloor(config.antiSnipeMaxBps, window - elapsed, window);
}

export interface BuyParams {
  /** TON, доступные для сделки: газ вызывающая сторона вычитает заранее. */
  tonIn: bigint;
  /** Минимальный приемлемый выход в наножетонах. */
  minOut?: bigint;
  /** Время блока, секунды. */
  now: number;
  /** Дедлайн из payload, секунды. */
  deadline?: number;
}

export interface BuyResult {
  antiSnipeFeeBps: bigint;
  /** Антиснайп-доля, вычтенная первой и осевшая в кривой. */
  antiSnipeFee: bigint;
  /** 1% на TON-ноге, считается от остатка после антиснайпа. */
  tradeFee: bigint;
  /** TON, вошедшие в `real_ton`. */
  tonToReserve: bigint;
  jettonsOut: bigint;
  /** Возврат покупателю при перелёте через порог. */
  refund: bigint;
  partial: boolean;
  thresholdReached: boolean;
  state: CurveState;
}

/**
 * Покупка за TON. Порядок вычетов из ADR-0007: сначала антиснайп-доля, затем 1%
 * от остатка, и только то, что осталось, увеличивает `real_ton` и обменивается
 * на жетоны. При частичном исполнении комиссии берутся только с исполненной
 * части — округление вверх, в пользу протокола.
 */
export function buy(config: CurveConfig, state: CurveState, params: BuyParams): BuyResult {
  if (state.phase !== 'Active') {
    fail('notActive', 'buy: curve is not Active');
  }
  if (params.tonIn <= 0n) {
    fail('zeroAmount', 'buy: tonIn must be positive');
  }
  if (params.deadline !== undefined && params.now > params.deadline) {
    fail('deadlinePassed', 'buy: deadline passed');
  }

  const feeBps = antiSnipeFeeBps(config, state, params.now);
  const antiSnipeFull = mulDivFloor(params.tonIn, feeBps, BPS_DENOM);
  const afterAntiSnipe = params.tonIn - antiSnipeFull;
  const tradeFeeFull = mulDivFloor(afterAntiSnipe, config.tradeFeeBps, BPS_DENOM);
  const dx = afterAntiSnipe - tradeFeeFull;

  const fill = fillOnCurve(config, state, dx);
  if (fill.jettons <= 0n) {
    fail('curveFull', 'buy: curve cannot accept this trade');
  }

  const antiSnipeFee = dx > 0n ? mulDivCeil(antiSnipeFull, fill.dxUsed, dx) : 0n;
  const tradeFee = dx > 0n ? mulDivCeil(tradeFeeFull, fill.dxUsed, dx) : 0n;
  const refund = params.tonIn - fill.dxUsed - antiSnipeFee - tradeFee;

  if (params.minOut !== undefined && fill.jettons < params.minOut) {
    fail('slippage', 'buy: min_out not satisfied');
  }

  const next: CurveState = {
    ...state,
    realTon: state.realTon + fill.dxUsed,
    antiSnipeTon: state.antiSnipeTon + antiSnipeFee,
    sold: state.sold + fill.jettons,
  };
  const thresholdReached = isCurveClosed(config, next);
  if (thresholdReached) {
    next.phase = 'Graduating';
  }

  return {
    antiSnipeFeeBps: feeBps,
    antiSnipeFee,
    tradeFee,
    tonToReserve: fill.dxUsed,
    jettonsOut: fill.jettons,
    refund,
    partial: fill.leftover > 0n,
    thresholdReached,
    state: next,
  };
}

export interface SellParams {
  jettonsIn: bigint;
  minOut?: bigint;
  now: number;
  deadline?: number;
}

export interface SellResult {
  /** `dx_gross = floor(X * dy / (Y + dy))` — сколько TON уходит из резерва. */
  tonGross: bigint;
  tradeFee: bigint;
  /** Выплата продавцу. */
  tonOut: bigint;
  state: CurveState;
}

/**
 * Продажа жетонов. Антиснайп-комиссия здесь не применяется никогда: контракт не
 * облагает налогом выход из позиции даже в первую секунду торговли (ADR-0007).
 */
export function sell(config: CurveConfig, state: CurveState, params: SellParams): SellResult {
  if (state.phase !== 'Active') {
    fail('notActive', 'sell: curve is not Active');
  }
  if (params.jettonsIn <= 0n) {
    fail('zeroAmount', 'sell: jettonsIn must be positive');
  }
  if (params.deadline !== undefined && params.now > params.deadline) {
    fail('deadlinePassed', 'sell: deadline passed');
  }
  if (params.jettonsIn > state.sold) {
    fail('reserveUnderflow', 'sell: more jettons than the curve ever sold');
  }

  const { x, y } = reserves(config, state);
  const tonGross = mulDivFloor(x, params.jettonsIn, y + params.jettonsIn);
  if (tonGross > state.realTon) {
    fail('reserveUnderflow', 'sell: payout exceeds curve reserve');
  }
  const tradeFee = mulDivFloor(tonGross, config.tradeFeeBps, BPS_DENOM);
  const tonOut = tonGross - tradeFee;
  if (params.minOut !== undefined && tonOut < params.minOut) {
    fail('slippage', 'sell: min_out not satisfied');
  }

  return {
    tonGross,
    tradeFee,
    tonOut,
    state: {
      ...state,
      realTon: state.realTon - tonGross,
      sold: state.sold - params.jettonsIn,
    },
  };
}

export interface SeedResult {
  /** TON сида, вошедшие в резерв кривой. */
  tonToReserve: bigint;
  /** Жетоны транша сида: уходят в локер, не деплоеру. */
  jettons: bigint;
  /** Сид сверх ёмкости кривой: дополнительная TON-нога пула. */
  excessTon: bigint;
  state: CurveState;
}

/**
 * Сид ликвидности: те же формулы кривой, но без комиссии 1% и без базы для 5%
 * при градуации. Жетоны записываются в транш локера (ADR-0006).
 */
export function applySeed(config: CurveConfig, state: CurveState, seedTon: bigint): SeedResult {
  if (state.phase !== 'Initializing') {
    fail('notActive', 'seed: only during Initializing');
  }
  if (seedTon < 0n) {
    fail('zeroAmount', 'seed: negative amount');
  }
  const fill = fillOnCurve(config, state, seedTon);
  return {
    tonToReserve: fill.dxUsed,
    jettons: fill.jettons,
    excessTon: fill.leftover,
    state: {
      ...state,
      realTon: state.realTon + fill.dxUsed,
      seedTon: state.seedTon + fill.dxUsed,
      excessSeedTon: state.excessSeedTon + fill.leftover,
      sold: state.sold + fill.jettons,
      seedJettons: state.seedJettons + fill.jettons,
    },
  };
}

export interface DevBuyResult {
  tradeFee: bigint;
  tonToReserve: bigint;
  jettons: bigint;
  /** Возврат деплоеру: перелёт через порог или закрытая сидом кривая. */
  refund: bigint;
  state: CurveState;
}

/**
 * Dev-buy внутри операции запуска: обычная покупка с 1% и лимитом 2% эмиссии,
 * но без антиснайп-комиссии — кривая ещё не `Active`, окно не открыто.
 * Если сид уже исчерпал кривую, dev-buy не исполняется и TON возвращаются.
 */
export function applyDevBuy(
  config: CurveConfig,
  state: CurveState,
  devBuyTon: bigint,
): DevBuyResult {
  if (state.phase !== 'Initializing') {
    fail('notActive', 'dev-buy: only during Initializing');
  }
  if (devBuyTon < 0n) {
    fail('zeroAmount', 'dev-buy: negative amount');
  }
  if (devBuyTon === 0n || isCurveClosed(config, state)) {
    return { tradeFee: 0n, tonToReserve: 0n, jettons: 0n, refund: devBuyTon, state };
  }

  const tradeFeeFull = mulDivFloor(devBuyTon, config.tradeFeeBps, BPS_DENOM);
  const dx = devBuyTon - tradeFeeFull;
  const fill = fillOnCurve(config, state, dx);
  const tradeFee = dx > 0n ? mulDivCeil(tradeFeeFull, fill.dxUsed, dx) : 0n;
  const refund = devBuyTon - fill.dxUsed - tradeFee;

  if (fill.jettons > devBuyLimit(config)) {
    fail('devBuyLimit', 'dev-buy: exceeds 2% of total supply');
  }

  return {
    tradeFee,
    tonToReserve: fill.dxUsed,
    jettons: fill.jettons,
    refund,
    state: {
      ...state,
      realTon: state.realTon + fill.dxUsed,
      sold: state.sold + fill.jettons,
      devBuyJettons: state.devBuyJettons + fill.jettons,
    },
  };
}

/**
 * Конец init: либо торговля открывается и с этого момента идёт отсчёт
 * антиснайп-окна, либо сид уже закрыл порог и публичной кривой не будет.
 */
export function openTrading(config: CurveConfig, state: CurveState, now: number): CurveState {
  if (state.phase !== 'Initializing') {
    fail('alreadyInitialized', 'openTrading: init already finished');
  }
  if (isCurveClosed(config, state)) {
    return { ...state, phase: 'Graduating' };
  }
  return { ...state, phase: 'Active', activatedAt: now };
}

export interface LaunchParams {
  seedTon: bigint;
  devBuyTon: bigint;
  now: number;
}

export interface LaunchResult {
  seed: SeedResult;
  devBuy: DevBuyResult;
  /** TON, которые фабрика возвращает деплоеру (неисполненная часть dev-buy). */
  refund: bigint;
  /** 1% с dev-buy — единственная комиссия операции запуска. */
  feeToVault: bigint;
  state: CurveState;
}

/** Полный init запуска: сид, затем dev-buy, затем открытие торговли. */
export function simulateLaunch(config: CurveConfig, params: LaunchParams): LaunchResult {
  const seed = applySeed(config, initialState(), params.seedTon);
  const devBuy = applyDevBuy(config, seed.state, params.devBuyTon);
  const state = openTrading(config, devBuy.state, params.now);
  return {
    seed,
    devBuy,
    refund: devBuy.refund,
    feeToVault: devBuy.tradeFee,
    state,
  };
}

export interface GraduationPreview {
  /** База 5%: публичные TON без сида и без антиснайп-накопителя. */
  publicTon: bigint;
  protocolCut: bigint;
  /** TON в пул: резерв минус доля протокола, плюс избыток сида и антиснайп. */
  poolTon: bigint;
  /** Цена финала кривой, нанотоны за целый жетон. */
  finalPrice: bigint;
  poolJettons: bigint;
  burnJettons: bigint;
}

/**
 * Разложение градуации по суммам. Здесь нет и не должно быть знания о DEX: адреса
 * и последовательность сообщений приходят параметром запуска, а этот расчёт
 * отвечает только на вопрос «сколько TON и жетонов уходит в пул».
 */
export function graduationPreview(config: CurveConfig, state: CurveState): GraduationPreview {
  const base = publicTon(state);
  const protocolCut = mulDivFloor(base, config.graduationFeeBps, BPS_DENOM);
  const poolTon = state.realTon - protocolCut + state.excessSeedTon + state.antiSnipeTon;
  const finalPrice = spotPrice(config, state);
  const reserve = poolReserve(config);
  const { x, y } = reserves(config, state);
  const wanted = mulDivFloor(poolTon, y, x);
  const poolJettons = min(reserve, wanted);
  return {
    publicTon: base,
    protocolCut,
    poolTon,
    finalPrice,
    poolJettons,
    burnJettons: reserve - poolJettons,
  };
}

/** Минимальный `dx`, которым можно получить ровно `jettons` по текущей цене. */
export function tonForJettons(
  config: CurveConfig,
  state: CurveState,
  jettons: bigint,
): bigint {
  const { x, y } = reserves(config, state);
  if (jettons <= 0n) {
    return 0n;
  }
  if (jettons >= y) {
    fail('curveFull', 'tonForJettons: amount exceeds the reserve');
  }
  return ceilDiv(x * jettons, y - jettons);
}
